/**
 * The Addis clock.
 *
 * Every date decision in the platform is made in East Africa Time. EAT is a
 * fixed UTC+3 with no daylight saving, so an instant becomes an Addis calendar
 * date by shifting three hours and reading the UTC fields, and back again by
 * shifting the other way. That keeps the arithmetic exact and needs no timezone
 * database.
 */

export const EAT_OFFSET_MINUTES = 180;

export interface GregorianDate {
  year: number;
  month: number;
  day: number;
}

/** The Gregorian date an instant falls on in Addis. */
export function toEatParts(instant: Date): GregorianDate {
  const shifted = new Date(instant.getTime() + EAT_OFFSET_MINUTES * 60_000);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  };
}

/** The instant a wall clock time in Addis corresponds to, as UTC. */
export function fromEatParts(date: GregorianDate, hour = 0, minute = 0): Date {
  const utcMillis = Date.UTC(date.year, date.month - 1, date.day, hour, minute);
  return new Date(utcMillis - EAT_OFFSET_MINUTES * 60_000);
}

/**
 * The Addis wall clock an instant reads on, 24 hour.
 *
 * Twenty four rather than twelve because there is no morning or evening word to
 * translate, and because the twelve hour clock Ethiopia keeps in speech starts
 * six hours off the one a phone shows. A record that says 2:00 and means either
 * eight in the morning or two in the afternoon is not a record.
 */
export function formatEatTime(instant: Date): string {
  const shifted = new Date(instant.getTime() + EAT_OFFSET_MINUTES * 60_000);
  const hour = String(shifted.getUTCHours()).padStart(2, "0");
  const minute = String(shifted.getUTCMinutes()).padStart(2, "0");
  return `${hour}:${minute}`;
}
